// Kanban view for Prospección — 3 columns: Sin visitar / Activos / Conflictivos.
// Cards navigate: with `code` → open SiteMasterModal in map; otherwise fly to coords.
import React, { useEffect, useState } from 'react';
import api from '../../../api/client';
import { Loader2, MapPin, AlertTriangle, Flag, CircleDot, Handshake, ShieldCheck, Calendar, ExternalLink, Building2, User } from 'lucide-react';

const COLUMNS = [
  { key: 'sin_visitar',  label: 'Sin visitar',   dot: '#facc15', accent: 'yellow',
    hint: 'Sites sin actividad registrada', empty: 'Todo el catálogo tiene alguna actividad 🎉' },
  { key: 'activos',      label: 'Activos',       dot: '#A3FF12', accent: 'green',
    hint: 'Con actividad en curso', empty: 'Sin sites activos ahora mismo' },
  { key: 'conflictivos', label: 'Conflictivos',  dot: '#ef4444', accent: 'red',
    hint: 'Marcados como conflictivos', empty: 'Sin sites conflictivos 👌' },
];

const SEV_COLORS = { alta: 'text-red-400 bg-red-500/10 border-red-500/30',
                     media: 'text-orange-300 bg-orange-500/10 border-orange-500/30',
                     baja: 'text-yellow-300 bg-yellow-500/10 border-yellow-500/30' };

const Card = ({ card, column, onOpen }) => {
  const c = card;
  const isConflict = column === 'conflictivos';
  return (
    <button onClick={() => onOpen(c)} data-testid={`board-card-${c.site_id}`}
      className={`w-full text-left bg-black/30 border rounded-xl p-3 hover:border-[#A3FF12]/50 hover:bg-black/50 transition-colors group ${
        c.has_high_incident ? 'border-red-500/40' :
        isConflict ? 'border-red-500/30' : 'border-white/10'
      }`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="text-white font-black text-sm truncate">{c.name}</div>
          {(c.code || c.municipality) && (
            <div className="text-white/50 text-[10px] mt-0.5 truncate flex items-center gap-1">
              {c.code && <span className="text-[#A3FF12] font-bold">{c.code}</span>}
              {c.code && c.municipality && <span className="text-white/20">·</span>}
              {c.municipality && <><MapPin size={9}/> {c.municipality}</>}
            </div>
          )}
        </div>
        <ExternalLink size={12} className="text-white/20 group-hover:text-[#A3FF12] flex-shrink-0 mt-0.5 transition-colors"/>
      </div>

      {c.owner_name && (
        <div className="text-white/40 text-[10px] flex items-center gap-1 truncate">
          <User size={9}/> {c.owner_name}
        </div>
      )}

      {/* Activos: badges de actividad */}
      {c.counts && (
        <div className="flex items-center gap-1 mt-2 flex-wrap">
          {c.counts.incidents > 0 && (
            <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${
              c.has_high_incident ? 'bg-red-500/15 text-red-300 border-red-500/30' : 'bg-orange-500/15 text-orange-300 border-orange-500/30'
            }`}>
              <AlertTriangle size={8}/> {c.counts.incidents}
            </span>
          )}
          {c.counts.negotiations > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-blue-500/15 text-blue-300 border-blue-500/30">
              <Handshake size={8}/> {c.counts.negotiations}
            </span>
          )}
          {c.counts.agenda > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-purple-500/15 text-purple-300 border-purple-500/30">
              <Calendar size={8}/> {c.counts.agenda}
            </span>
          )}
          {c.counts.preventive > 0 && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border bg-[#A3FF12]/15 text-[#A3FF12] border-[#A3FF12]/30">
              <ShieldCheck size={8}/> {c.counts.preventive}
            </span>
          )}
        </div>
      )}

      {/* Conflictivo: motivo + severidad */}
      {c.conflict && (
        <div className="mt-2 flex items-start gap-1.5">
          <span className={`inline-flex items-center gap-0.5 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full border ${SEV_COLORS[c.conflict.severity] || SEV_COLORS.media}`}>
            <Flag size={8}/> {c.conflict.severity}
          </span>
          <span className="text-white/60 text-[10px] line-clamp-2">{c.conflict.description || c.conflict.reason.replace(/_/g, ' ')}</span>
        </div>
      )}

      {/* Sin visitar: prioridad + coverage */}
      {!c.counts && !c.conflict && (
        <div className="mt-2 flex items-center gap-1.5 text-[9px]">
          {c.priority && (
            <span className="font-bold text-white/60 border border-white/10 px-1.5 py-0.5 rounded-full">{c.priority}</span>
          )}
          {c.coverage_quality && (
            <span className={`font-bold px-1.5 py-0.5 rounded-full ${
              c.coverage_quality === 'baja' ? 'text-red-300 bg-red-500/10' :
              c.coverage_quality === 'media' ? 'text-yellow-300 bg-yellow-500/10' :
              'text-[#A3FF12] bg-[#A3FF12]/10'
            }`}>cobertura {c.coverage_quality}</span>
          )}
        </div>
      )}
    </button>
  );
};

const ProspeccionBoard = ({ onOpenSite }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/prospeccion/board', { params: { limit: 50 } });
        if (!cancel) setData(data);
      } catch { /* noop */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>;
  if (!data) return <div className="text-center py-16 text-white/40 text-sm">Sin datos.</div>;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="prospeccion-board">
      {COLUMNS.map((col) => {
        const items = data[col.key] || [];
        const total = data.counts?.[col.key] ?? items.length;
        return (
          <div key={col.key} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 min-h-[400px]"
            data-testid={`board-col-${col.key}`}>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/5">
              <div className="flex items-center gap-2">
                <span style={{ background: col.dot, width: 10, height: 10, borderRadius: '50%' }}/>
                <h3 className="text-white font-black text-base">{col.label}</h3>
                <span className="text-white/40 text-xs">({total.toLocaleString('es-ES')})</span>
              </div>
              <CircleDot size={12} className="text-white/20"/>
            </div>
            <p className="text-white/40 text-[10px] mb-3">{col.hint}</p>
            <div className="space-y-2 max-h-[68vh] overflow-y-auto pr-1 -mr-1">
              {items.length === 0 ? (
                <div className="text-center py-10 text-white/30 text-xs">{col.empty}</div>
              ) : items.map((card) => (
                <Card key={card.site_id} card={card} column={col.key} onOpen={onOpenSite}/>
              ))}
              {items.length < total && (
                <div className="text-center py-2 text-white/30 text-[10px]">
                  Mostrando {items.length} de {total}.
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProspeccionBoard;
